import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, MapPin, Calculator, Save, Eye, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api, errMsg, peso, parseCents, centsToInput, uuid } from "../lib/api";
import { saveLocal } from "../lib/offline";
import { Btn, Field, Input, Select, TextArea, Card, PageHeader, Money } from "../components/common";
import ScopePicker from "../components/ScopePicker";
import { DocumentUpload } from "../components/Documents";

const nowLocal = () => new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 16);

function blank(user, meta) {
  return {
    client_uuid: uuid(), gkk_id: user.gkk_id || meta?.gkks?.[0]?.id || "", unit_id: user.unit_id || "", report_type: "collection",
    occasion_key: "wednesday_bible_service", occasion_other: "", service_date: meta?.today || nowLocal().slice(0, 10), start_time: "", end_time: "",
    counted_at: nowLocal(), location: "", venue: "", gps: null, lines: [{ category_id: "", amount: "" }], denominations: {}, declared_total: "",
    treasurer_name: user.name, position: user.position || "", contact: user.contact || "", witness_name: "", custodian_name: "", certified: false,
    attachment_note: "", remarks: "", attachments: [], documents_confirmed: false, deductions: "", deductions_note: "", amount_remitted: "", discrepancy_explanation: "",
  };
}

export function recon(f, cats, denoms) {
  const amt = (l) => parseCents(l.amount) || 0;
  const isCol = f.report_type === "collection";
  const total = isCol ? f.lines.reduce((s, l) => s + amt(l), 0) : 0;
  const parish = isCol ? f.lines.filter((l) => cats.find((c) => c.id === l.category_id)?.restricted).reduce((s, l) => s + amt(l), 0) : 0;
  const deduct = f.deductions ? parseCents(f.deductions) || 0 : 0;
  const remitted = f.amount_remitted ? parseCents(f.amount_remitted) || 0 : 0;
  const counted = (denoms || []).reduce((s, d) => s + d * (Number(f.denominations?.[d]) || 0), 0);
  const declared = isCol && f.declared_total ? parseCents(f.declared_total) : null;
  const diffs = [];
  if (declared !== null && declared !== total) diffs.push(["Hand-written total vs entered amounts", total - declared]);
  if (isCol && counted > 0 && counted !== total) diffs.push(["Counted bills & coins vs entered amounts", total - counted]);
  if (remitted > parish) diffs.push(["Remitted more than the parish allocation", remitted - parish]);
  if (total - parish - deduct < 0) diffs.push(["Deductions exceed the GKK share", total - parish - deduct]);
  return { total, parish, deduct, retained: total - parish - deduct, remitted, outstanding: Math.max(parish - remitted, 0), diffs };
}

function Recon({ f, setF, r }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const rows = [["Total Collection", r.total, "recon-total"], ["Amount Allocated to the Parish (designated)", r.parish, "recon-parish"], ["Amount Retained by the GKK", r.retained, "recon-retained"],
    ["Other Authorized Deductions", r.deduct, "recon-deductions"], ["Actual Amount Remitted (declared)", r.remitted, "recon-remitted"], ["Outstanding Amount for Remittance", r.outstanding, "recon-outstanding"]];
  return (
    <div className="space-y-4" data-testid="reconciliation-panel">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Other authorized deductions ₱ (if any)"><Input data-testid="deductions-input" inputMode="decimal" className="font-mono" value={f.deductions} onChange={set("deductions")} placeholder="0.00" /></Field>
        <Field label="Amount remitted to the parish with this report ₱" hint="The Parish Office confirms what it actually receives separately."><Input data-testid="amount-remitted-input" inputMode="decimal" className="font-mono" value={f.amount_remitted} onChange={set("amount_remitted")} placeholder="0.00" /></Field>
      </div>
      {r.deduct > 0 && <Field label="Explain the deductions (required)"><Input data-testid="deductions-note-input" value={f.deductions_note} onChange={set("deductions_note")} /></Field>}
      <div className="rounded-xl border border-[#E4DACD] overflow-hidden">
        {rows.map(([a, c, id]) => <div key={id} className="flex justify-between px-3 py-2 border-b border-[#F3EDE2] text-sm"><span>{a}</span><span className="font-mono font-semibold" data-testid={id}>{peso(c)}</span></div>)}
        <div className={`px-3 py-2.5 text-sm font-bold ${r.diffs.length ? "bg-[#FEF2F2] text-[#B91C1C]" : "bg-[#E8F5E9] text-[#1B5E3F]"}`} data-testid="recon-discrepancy">
          {r.diffs.length ? r.diffs.map(([a, c]) => <div key={a}>Discrepancy — {a}: {peso(c)}</div>) : "Difference or Discrepancy: none"}
        </div>
      </div>
      {r.diffs.length > 0 && <Field label="Explain the discrepancy (required before submitting)"><TextArea data-testid="discrepancy-explanation-input" value={f.discrepancy_explanation} onChange={set("discrepancy_explanation")} /></Field>}
    </div>
  );
}

function Denoms({ f, set, denoms }) {
  const { t } = useI18n();
  const total = denoms.reduce((s, d) => s + d * (Number(f.denominations[d]) || 0), 0);
  const lines = f.lines.reduce((s, l) => s + (parseCents(l.amount) || 0), 0);
  const declared = f.declared_total ? parseCents(f.declared_total) : null;
  return (
    <Card className="bg-[#FEF9E7] border-[#EADFB4]">
      <div className="flex items-center gap-2 font-bold text-[#5C4033] mb-3"><Calculator className="h-5 w-5" /> {t("denom_counter")}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {denoms.map((d) => (
          <div key={d} className="rounded-xl bg-white border border-[#EADFB4] p-2.5">
            <div className="text-sm font-bold text-[#5C4033]">{d >= 2000 && d !== 2000 ? "Bill" : d === 2000 ? "Bill/Coin" : "Coin"} {peso(d).replace(".00", "")}</div>
            <input data-testid={`input-denomination-${d}`} inputMode="numeric" value={f.denominations[d] || ""} placeholder="0"
              onChange={(e) => set({ ...f, denominations: { ...f.denominations, [d]: e.target.value.replace(/\D/g, "") } })}
              className="mt-1 w-full h-11 rounded-lg border-2 border-[#5C4033]/20 px-2 text-lg font-mono" />
            <div className="text-xs text-[#6E6259] mt-1 font-mono">= {peso(d * (Number(f.denominations[d]) || 0))}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
        <div data-testid="denomination-total">Counted bills & coins: <b className="font-mono">{peso(total)}</b></div>
        <div>Collection lines: <b className="font-mono">{peso(lines)}</b></div>
        {total > 0 && total !== lines && <div data-testid="denomination-difference" className="font-bold text-[#B91C1C]">Difference: {peso(lines - total)} — please recount. Figures are not changed automatically.</div>}
        {declared !== null && total > 0 && declared !== total && <div data-testid="declared-difference" className="font-bold text-[#B91C1C]">Hand total vs count: {peso(declared - total)}</div>}
      </div>
    </Card>
  );
}

function Lines({ f, set, cats }) {
  const { t } = useI18n();
  const upd = (i, k, v) => set({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) });
  const groups = [["regular", t("regular")], ["special", t("special")], ["other", t("other")]];
  return (
    <div className="space-y-3">
      {f.lines.map((l, i) => {
        const c = cats.find((x) => x.id === l.category_id);
        const bad = parseCents(l.amount) === null;
        return (
          <div key={i} data-testid={`collection-line-${i}`} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_200px_auto] gap-3 items-end rounded-xl bg-[#FAF7F2] p-3 border border-[#EFE7DB]">
            <Field label={`${t("category")} #${i + 1}`} className="col-span-2 sm:col-span-1" hint={c?.restricted ? "Designated (restricted) — kept separate from GKK operating funds" : null}>
              <Select data-testid={`line-category-select-${i}`} value={l.category_id} onChange={(e) => upd(i, "category_id", e.target.value)}>
                <option value="">— Choose —</option>
                {groups.map(([g, label]) => (
                  <optgroup key={g} label={label}>{cats.filter((x) => x.group === g).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</optgroup>
                ))}
              </Select>
            </Field>
            <Field label={t("amount")} hint={bad ? "Use numbers only, e.g. 1250.00" : null}>
              <Input data-testid={`line-amount-input-${i}`} inputMode="decimal" placeholder="0.00" value={l.amount} onChange={(e) => upd(i, "amount", e.target.value)} className={`font-mono ${bad ? "border-[#B91C1C]" : ""}`} />
            </Field>
            <button type="button" data-testid={`line-remove-${i}`} disabled={f.lines.length === 1} onClick={() => set({ ...f, lines: f.lines.filter((_, j) => j !== i) })} className="h-12 w-12 rounded-xl text-[#B91C1C] hover:bg-[#FEF2F2] disabled:opacity-30 inline-flex items-center justify-center" aria-label="Remove line"><Trash2 /></button>
          </div>
        );
      })}
      <Btn v="outline" type="button" data-testid="add-collection-line-button" onClick={() => set({ ...f, lines: [...f.lines, { category_id: "", amount: "" }] })}><Plus className="h-5 w-5" /> {t("add_line")}</Btn>
    </div>
  );
}

function Review({ f, meta, cats, onBack, onSubmit, busy, r }) {
  const { t } = useI18n();
  const g = meta.gkks.find((x) => x.id === f.gkk_id);
  const u = meta.units.find((x) => x.id === f.unit_id);
  const occ = meta.categories.find((c) => c.kind === "occasion" && c.key === f.occasion_key);
  const total = f.lines.reduce((s, l) => s + (parseCents(l.amount) || 0), 0);
  const rows = [["GKK / Unit", `${g?.name} · ${u?.name || "GKK-level"}`], ["Activity", f.occasion_key === "other" ? f.occasion_other : occ?.name],
    ["Report", t(`rt_${f.report_type}`)], ["Date & time", `${f.service_date} ${f.start_time || ""}${f.end_time ? "–" + f.end_time : ""}`], ["Counted at", f.counted_at?.replace("T", " ")],
    ["Location / Venue", `${f.location || "—"} · ${f.venue || "—"}`], ["Treasurer", `${f.treasurer_name} (${f.position || "—"})`], ["Witness", f.witness_name || "—"],
    ["Supporting evidence", f.attachment_note || "—"], ["Signed documents uploaded", `${f.attachments.length} file(s)${f.documents_confirmed ? " · legibility confirmed" : ""}`],
    ["Parish allocation / retained by GKK", `${peso(r.parish)} / ${peso(r.retained)}`], ["Remitted (declared) / outstanding", `${peso(r.remitted)} / ${peso(r.outstanding)}`]];
  const problems = [!f.attachments.length && "Upload the signed collection document", f.attachments.length && !f.documents_confirmed && "Confirm the uploaded document is clearly readable",
    r.diffs.length && !f.discrepancy_explanation.trim() && "Explain the discrepancy", r.deduct > 0 && !f.deductions_note.trim() && "Explain the deductions", !f.certified && "Tick the certification of accuracy"].filter(Boolean);
  return (
    <div data-testid="review-panel" className="space-y-4">
      <div className="space-y-1.5 text-sm">{rows.map(([a, b]) => <div key={a} className="flex justify-between gap-4 border-b border-[#F3EDE2] pb-1.5"><span className="text-[#6E6259]">{a}</span><span className="font-semibold text-right">{b}</span></div>)}</div>
      {f.report_type === "collection" && (
        <div className="rounded-xl border border-[#E4DACD] overflow-hidden">
          {f.lines.map((l, i) => { const c = cats.find((x) => x.id === l.category_id); return (
            <div key={i} className="flex justify-between px-3 py-2 border-b border-[#F3EDE2] text-sm"><span>{c?.name} {c?.restricted && <em className="text-[#C85A32] not-italic font-semibold">· designated</em>}</span><Money c={parseCents(l.amount) || 0} /></div>); })}
          <div className="flex justify-between px-3 py-3 bg-[#F3EDE2] font-bold"><span>Grand total</span><Money c={total} testid="review-grand-total" /></div>
        </div>
      )}
      <p className="rounded-xl bg-[#FEF9E7] border border-[#EADFB4] p-3 text-sm">“{t("certify")}” — {f.certified ? <b className="text-[#1B5E3F]">Certified ✓</b> : <b className="text-[#B91C1C]">Not yet certified</b>}</p>
      <p className="rounded-xl bg-[#FEF3C7] p-3 text-sm font-semibold text-[#92400E]">{t("review_warning")}</p>
      {problems.length > 0 && <div className="rounded-xl bg-[#FEF2F2] border border-[#FECACA] p-3 text-sm text-[#B91C1C]" data-testid="review-blockers"><b>Before you can submit:</b><ul className="list-disc pl-5">{problems.map((p) => <li key={p}>{p}</li>)}</ul></div>}
      <div className="flex flex-col sm:flex-row gap-3">
        <Btn v="outline" onClick={onBack} data-testid="review-back-button" className="flex-1">{t("back_edit")}</Btn>
        <Btn v="green" onClick={onSubmit} loading={busy} disabled={problems.length > 0} data-testid="review-confirm-submit-button" className="flex-1"><Send className="h-5 w-5" /> {t("confirm_submit")}</Btn>
      </div>
    </div>
  );
}

export default function CollectionForm() {
  const { id } = useParams();
  const { user, meta, offline } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [f, setF] = useState(() => blank(user, meta));
  const [serverId, setServerId] = useState(id || null);
  const [review, setReview] = useState(false);
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDen, setShowDen] = useState(false);
  const cats = useMemo(() => (meta?.categories || []).filter((c) => c.kind === "collection" && c.active && !c.own_page), [meta]);
  const occs = (meta?.categories || []).filter((c) => c.kind === "occasion" && c.active);

  useEffect(() => {
    if (!id) return;
    api.get(`/collections/${id}`).then(({ data }) => {
      setF({ ...blank(user, meta), ...data, unit_id: data.unit_id || "", declared_total: centsToInput(data.declared_total_cents),
        attachments: data.attachments || [], documents_confirmed: !!data.documents_confirmed, deductions: data.reconciliation?.deductions_cents ? centsToInput(data.reconciliation.deductions_cents) : "",
        amount_remitted: data.reconciliation?.remitted_cents ? centsToInput(data.reconciliation.remitted_cents) : "", deductions_note: data.deductions_note || "", discrepancy_explanation: data.discrepancy_explanation || "",
        lines: data.lines.length ? data.lines.map((l) => ({ line_id: l.line_id, category_id: l.category_id, amount: centsToInput(l.amount_cents) })) : [{ category_id: "", amount: "" }] });
      setShowDen(Object.keys(data.denominations || {}).length > 0);
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = (submit, confirm = false) => ({ ...f, attachments: undefined, attachment_ids: f.attachments.map((a) => a.id), unit_id: f.unit_id || null, lines: f.report_type === "collection" ? f.lines.filter((l) => l.category_id || l.amount) : [],
    denominations: Object.fromEntries(Object.entries(f.denominations || {}).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])), submit, confirm_duplicates: confirm });

  const saveDraft = async () => {
    if (offline) { saveLocal(user.id, payload(false)); toast.success("Saved on this phone. It will upload when internet returns. Not yet an official submission."); nav("/collections"); return; }
    setBusy(true);
    try {
      const { data } = serverId ? await api.put(`/collections/${serverId}`, payload(false)) : await api.post("/collections", payload(false));
      setServerId(data.id);
      toast.success(`Draft saved (${data.draft_no || data.ref_no}). Not yet submitted.`);
      nav(`/collections/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submit = async (confirm = false) => {
    setBusy(true);
    try {
      let data;
      if (confirm && serverId) data = (await api.post(`/collections/${serverId}/submit`, { confirm_duplicates: true })).data;
      else data = (serverId ? await api.put(`/collections/${serverId}`, payload(true, confirm)) : await api.post("/collections", payload(true, confirm))).data;
      toast.success(`Submitted and locked. Reference ${data.ref_no}`);
      nav(`/collections/${data.id}?submitted=1`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.draft_id) setServerId(d.draft_id);
      if (d?.code === "possible_duplicate") { setReview(false); setDup(d); } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const openReview = () => {
    if (!f.gkk_id) return toast.error("Please choose the GKK");
    if (f.report_type === "collection" && f.lines.some((l) => !l.category_id || parseCents(l.amount) === null || !l.amount)) return toast.error("Each collection needs a category and a valid amount");
    setReview(true);
  };

  if (!meta) return <p>Loading…</p>;
  const r = recon(f, cats, meta.settings.denominations);
  const n = f.report_type === "collection" ? 3 : 2;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="max-w-4xl">
      <PageHeader title={id ? "Edit Collection Report" : t("record_collection")} subtitle="Fill in the details right after counting. Times are Philippine Standard Time." back="/collections" />
      <div className="space-y-6">
        <Card className="space-y-5">
          <h2 className="text-lg font-bold text-[#5C4033]">1 · {t("where_when")}</h2>
          <ScopePicker gkk={f.gkk_id} unit={f.unit_id} forWrite onChange={(g, u) => setF({ ...f, gkk_id: g, unit_id: u })} testid="collection" />
          <Field label={t("report_type")}>
            <div className="grid sm:grid-cols-3 gap-2">
              {["collection", "no_collection", "service_canceled"].map((rt) => (
                <button key={rt} type="button" data-testid={`report-type-${rt}`} onClick={() => setF({ ...f, report_type: rt })}
                  className={`h-14 rounded-xl border-2 font-bold ${f.report_type === rt ? "border-[#5C4033] bg-[#5C4033] text-white" : "border-[#E4DACD] bg-white"}`}>{t(`rt_${rt}`)}</button>
              ))}
            </div>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("occasion")}>
              <Select data-testid="occasion-select" value={f.occasion_key} onChange={set("occasion_key")}>{occs.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}</Select>
            </Field>
            {f.occasion_key === "other" && <Field label="Specify activity"><Input data-testid="occasion-other-input" value={f.occasion_other} onChange={set("occasion_other")} /></Field>}
            <Field label={t("date_collection")}><Input data-testid="service-date-input" type="date" max={meta.today} value={f.service_date} onChange={set("service_date")} /></Field>
            <Field label={t("start_time")}><Input data-testid="start-time-input" type="time" value={f.start_time} onChange={set("start_time")} /></Field>
            <Field label={t("end_time")}><Input data-testid="end-time-input" type="time" value={f.end_time} onChange={set("end_time")} /></Field>
            <Field label={t("counted_at")}><Input data-testid="counted-at-input" type="datetime-local" value={f.counted_at} onChange={set("counted_at")} /></Field>
            <Field label={t("location")}><Input data-testid="location-input" value={f.location} onChange={set("location")} placeholder="e.g. Purok Malipayon" /></Field>
            <Field label={t("venue")}><Input data-testid="venue-input" value={f.venue} onChange={set("venue")} placeholder="e.g. Unit 1 Chapel" /></Field>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Btn v="ghost" sm type="button" data-testid="gps-capture-button" onClick={() => navigator.geolocation?.getCurrentPosition((p) => setF((x) => ({ ...x, gps: { lat: p.coords.latitude, lng: p.coords.longitude } })), () => toast.error("Location permission not given"))}>
              <MapPin className="h-4 w-4" /> Add GPS location (optional, with your permission)
            </Btn>
            {f.gps && <span className="text-sm text-[#1B5E3F]">GPS saved ✓ <button className="underline ml-2" onClick={() => setF({ ...f, gps: null })}>remove</button></span>}
          </div>
        </Card>

        {f.report_type === "collection" && (
          <Card className="space-y-5">
            <h2 className="text-lg font-bold text-[#5C4033]">2 · {t("amounts")}</h2>
            <p className="text-sm text-[#6E6259]">Enter each collection separately. Special collections stay designated for their beneficiary and are never mixed with GKK operating money.</p>
            <Lines f={f} set={setF} cats={cats} />
            <div className="flex items-center justify-between rounded-xl bg-[#5C4033] text-white px-5 py-4">
              <span className="font-bold">{t("total")} (preview)</span>
              <span data-testid="collection-total-preview" className="font-mono text-2xl font-bold">{peso(f.lines.reduce((s, l) => s + (parseCents(l.amount) || 0), 0))}</span>
            </div>
            <Field label={t("declared_total")} hint="If you wrote a total on paper, enter it here so the system can compare."><Input data-testid="declared-total-input" inputMode="decimal" value={f.declared_total} onChange={set("declared_total")} className="font-mono" /></Field>
            <Btn v="outline" sm type="button" data-testid="toggle-denomination-counter" onClick={() => setShowDen(!showDen)}><Calculator className="h-4 w-4" /> {t("denom_counter")}</Btn>
            {showDen && <Denoms f={f} set={setF} denoms={meta.settings.denominations} />}
          </Card>
        )}

        {f.report_type === "collection" && (
          <Card className="space-y-5">
            <h2 className="text-lg font-bold text-[#5C4033]">{n} · Reconciliation</h2>
            <p className="text-sm text-[#6E6259]">Calculated exactly from the amounts you entered. The amounts written on the uploaded document are not read automatically; the Parish Office checks them.</p>
            <Recon f={f} setF={setF} r={r} />
          </Card>
        )}

        <Card className="space-y-5">
          <h2 className="text-lg font-bold text-[#5C4033]" data-testid="document-section-title">{n + (f.report_type === "collection" ? 1 : 0)} · Upload Signed Collection and Remittance Verification Document</h2>
          <DocumentUpload gkkId={f.gkk_id} unitId={f.unit_id} items={f.attachments} onChange={(a) => setF((x) => ({ ...x, attachments: a }))}
            confirmed={f.documents_confirmed} onConfirm={(v) => setF((x) => ({ ...x, documents_confirmed: v }))} offline={offline} />
        </Card>

        <Card className="space-y-5">
          <h2 className="text-lg font-bold text-[#5C4033]">{n + (f.report_type === "collection" ? 2 : 1)} · {t("people_cert")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("treasurer_name")}><Input data-testid="treasurer-name-input" value={f.treasurer_name} onChange={set("treasurer_name")} /></Field>
            <Field label={t("position")}><Input data-testid="position-input" value={f.position} onChange={set("position")} /></Field>
            <Field label="Contact number"><Input data-testid="contact-input" inputMode="tel" value={f.contact} onChange={set("contact")} /></Field>
            <Field label={t("witness")}><Input data-testid="witness-input" value={f.witness_name} onChange={set("witness_name")} /></Field>
            <Field label={t("custodian")}><Input data-testid="custodian-input" value={f.custodian_name} onChange={set("custodian_name")} /></Field>
            <Field label="Count sheet / supporting record (reference)"><Input data-testid="attachment-note-input" value={f.attachment_note} onChange={set("attachment_note")} placeholder="e.g. Signed count sheet kept by Unit secretary" /></Field>
          </div>
          <Field label="Remarks"><TextArea data-testid="remarks-input" value={f.remarks} onChange={set("remarks")} /></Field>
          <label className="flex gap-3 items-start rounded-xl border-2 border-[#D4AF37] bg-[#FEF9E7] p-4 cursor-pointer">
            <input data-testid="certify-checkbox" type="checkbox" checked={f.certified} onChange={(e) => setF({ ...f, certified: e.target.checked })} className="mt-1 h-6 w-6 accent-[#5C4033]" />
            <span className="text-base">{t("certify")}<br /><b>— {f.treasurer_name || "…"}</b></span>
          </label>
        </Card>

        <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-[#FAF7F2]/95 backdrop-blur border-t border-[#E4DACD] p-4 sm:rounded-2xl grid grid-cols-1 sm:grid-cols-3 gap-3 no-print">
          <Btn v="outline" onClick={saveDraft} loading={busy && !review} data-testid="save-draft-button"><Save className="h-5 w-5" /> {t("save_draft")}</Btn>
          <Btn v="terra" onClick={openReview} disabled={offline} data-testid="review-entry-button"><Eye className="h-5 w-5" /> {t("review_entry")}</Btn>
          <Btn v="green" onClick={openReview} disabled={offline} data-testid="btn-submit-collection"><Send className="h-5 w-5" /> {t("submit_final")}</Btn>
        </div>
      </div>

      <Dialog open={review} onOpenChange={setReview}>
        <DialogContent className="max-w-2xl bg-white max-h-[92vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{t("review_entry")}</DialogTitle></DialogHeader>
          <Review f={f} meta={meta} cats={cats} r={r} busy={busy} onBack={() => setReview(false)} onSubmit={() => submit(false)} />
        </DialogContent>
      </Dialog>
      <Dialog open={!!dup} onOpenChange={() => setDup(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Possible duplicate report</DialogTitle></DialogHeader>
          <p data-testid="duplicate-warning-text">{dup?.message}</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <Btn v="outline" onClick={() => setDup(null)} data-testid="duplicate-cancel-button">Cancel — it is a duplicate</Btn>
            <Btn v="terra" onClick={() => { setDup(null); submit(true); }} data-testid="duplicate-confirm-button">It is a separate collection — submit</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
